import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Papa from 'papaparse';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Search, Edit, Trash2, Mail, Phone, Building2, Copy, Check, Upload, ChevronDown, FileSpreadsheet } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Clients = ({ user }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCsvDialogOpen, setIsCsvDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    notes: '',
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, formData);
        toast.success('Client updated');
      } else {
        await axios.post(`${API}/clients`, formData);
        toast.success('Client created');
      }

      fetchClients();
      resetForm();
      setIsDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      await axios.delete(`${API}/clients/${id}`);
      toast.success('Client deleted');
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email,
      phone: client.phone || '',
      company: client.company || '',
      address: client.address || '',
      notes: client.notes || '',
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      address: '',
      notes: '',
    });
    setEditingClient(null);
  };

  const copyClientId = (clientId) => {
    navigator.clipboard.writeText(clientId);
    setCopiedId(clientId);
    toast.success('Client ID copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setCsvFile(file);
    setCsvErrors([]);
    setCsvData([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const errors = [];
        const validData = [];

        results.data.forEach((row, index) => {
          const rowNum = index + 2;
          const rowErrors = [];

          // Validate required fields
          if (!row.name?.trim()) {
            rowErrors.push(`Row ${rowNum}: Name is required`);
          }
          if (!row.email?.trim()) {
            rowErrors.push(`Row ${rowNum}: Email is required`);
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
            rowErrors.push(`Row ${rowNum}: Invalid email format`);
          }

          if (rowErrors.length > 0) {
            errors.push(...rowErrors);
          } else {
            validData.push({
              name: row.name.trim(),
              email: row.email.trim(),
              phone: row.phone?.trim() || '',
              company: row.company?.trim() || '',
              address: row.address?.trim() || '',
              notes: row.notes?.trim() || '',
            });
          }
        });

        setCsvErrors(errors);
        setCsvData(validData);

        if (validData.length > 0 && errors.length === 0) {
          toast.success(`Parsed ${validData.length} valid client(s)`);
        } else if (errors.length > 0) {
          toast.error(`Found ${errors.length} error(s) in CSV`);
        }
      },
      error: (error) => {
        toast.error(`CSV parsing error: ${error.message}`);
      },
    });
  };

  const handleCsvUpload = async () => {
    if (csvData.length === 0) {
      toast.error('No valid data to upload');
      return;
    }

    setIsUploading(true);
    try {
      const response = await axios.post(`${API}/clients/bulk`, {
        clients: csvData,
      });

      toast.success(`Successfully created ${response.data.created} client(s)`);

      if (response.data.failed > 0) {
        const errorDetails = response.data.errors.map((err) =>
          `Client ${err.name}: ${err.error}`
        ).join('\n');

        console.error('Failed clients:', errorDetails);
      }

      fetchClients();
      setIsCsvDialogOpen(false);
      setCsvFile(null);
      setCsvData([]);
      setCsvErrors([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload clients');
    } finally {
      setIsUploading(false);
    }
  };

  const downloadCsvTemplate = () => {
    const template = `name,email,phone,company,address,notes
John Smith,john.smith@example.com,555-0101,ABC Construction,"123 Main St, New York, NY 10001",Primary contact for building projects
Sarah Johnson,sarah.j@buildersco.com,555-0102,Builders Co,"456 Oak Ave, Brooklyn, NY 11201",Payment terms: Net 30
Mike Williams,mike@renovations.com,555-0103,Williams Renovations,"789 Pine Rd, Queens, NY 11354",Preferred contractor for residential work`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'client_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (client.company && client.company.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Layout user={user}>
      <div className="p-4 md:p-8" data-testid="clients-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 md:mb-8 gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-4xl text-slate-900 mb-1 md:mb-2">Clients</h1>
            <p className="text-sm md:text-base text-slate-600">Manage your client database</p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-none btn-primary flex-1 sm:flex-none">
                  <Plus className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Add Clients</span>
                  <span className="sm:hidden">Add</span>
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => setIsDialogOpen(true)} className="cursor-pointer">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Single Client
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsCsvDialogOpen(true)} className="cursor-pointer">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogContent className="max-w-2xl rounded-none">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">
                  {editingClient ? 'Edit Client' : 'New Client'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="client-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      data-testid="client-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="rounded-sm border-2 h-10"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      data-testid="client-email-input"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="rounded-sm border-2 h-10"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      data-testid="client-phone-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="rounded-sm border-2 h-10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Company</Label>
                    <Input
                      data-testid="client-company-input"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="rounded-sm border-2 h-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    data-testid="client-address-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="rounded-sm border-2 h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input
                    data-testid="client-notes-input"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="rounded-sm border-2 h-10"
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button type="submit" data-testid="client-save-button" className="flex-1 bg-slate-900 hover:bg-slate-800 rounded-none">
                    {editingClient ? 'Update' : 'Create'}
                  </Button>
                  <Button
                    type="button"
                    data-testid="client-cancel-button"
                    variant="outline"
                    onClick={() => { setIsDialogOpen(false); resetForm(); }}
                    className="flex-1 rounded-none"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* CSV Upload Dialog */}
          <Dialog open={isCsvDialogOpen} onOpenChange={setIsCsvDialogOpen}>
            <DialogContent className="max-w-3xl rounded-none max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Upload Clients CSV</DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                {/* Template Download */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-none">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900 mb-1">Need a template?</h4>
                      <p className="text-sm text-slate-600">
                        Download our CSV template to see the required format and fields.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadCsvTemplate}
                      className="rounded-none border-slate-300"
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Download Template
                    </Button>
                  </div>
                </div>

                {/* File Upload */}
                <div className="space-y-2">
                  <Label>Select CSV File</Label>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={handleCsvFileChange}
                    className="rounded-sm border-2 h-12 cursor-pointer"
                  />
                  {csvFile && (
                    <p className="text-sm text-slate-600">
                      Selected: <span className="font-medium">{csvFile.name}</span>
                    </p>
                  )}
                </div>

                {/* Validation Errors */}
                {csvErrors.length > 0 && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-none">
                    <h4 className="font-semibold text-red-900 mb-2">Validation Errors ({csvErrors.length})</h4>
                    <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                      {csvErrors.slice(0, 10).map((error, idx) => (
                        <li key={idx} className="font-mono-data text-xs">• {error}</li>
                      ))}
                      {csvErrors.length > 10 && (
                        <li className="text-red-600 font-semibold">
                          ... and {csvErrors.length - 10} more error(s)
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                {/* Preview */}
                {csvData.length > 0 && csvErrors.length === 0 && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-none">
                    <h4 className="font-semibold text-green-900 mb-2">
                      Ready to Upload ({csvData.length} client{csvData.length !== 1 ? 's' : ''})
                    </h4>
                    <div className="max-h-60 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-green-100 sticky top-0">
                          <tr className="text-left">
                            <th className="p-2 font-semibold">Name</th>
                            <th className="p-2 font-semibold">Email</th>
                            <th className="p-2 font-semibold">Company</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-green-200">
                          {csvData.map((client, idx) => (
                            <tr key={idx} className="hover:bg-green-100">
                              <td className="p-2 font-mono-data">{client.name}</td>
                              <td className="p-2 font-mono-data">{client.email}</td>
                              <td className="p-2">{client.company || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* CSV Format Guide */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-none text-sm">
                  <h4 className="font-semibold text-slate-900 mb-2">CSV Format Requirements:</h4>
                  <ul className="text-slate-600 space-y-1">
                    <li>• <strong>Required fields:</strong> name, email</li>
                    <li>• <strong>Optional fields:</strong> phone, company, address, notes</li>
                    <li>• <strong>Email format:</strong> Must be valid email addresses</li>
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleCsvUpload}
                    disabled={csvData.length === 0 || csvErrors.length > 0 || isUploading}
                    className="flex-1 bg-slate-900 hover:bg-slate-800 rounded-none"
                  >
                    {isUploading ? 'Uploading...' : `Upload ${csvData.length} Client${csvData.length !== 1 ? 's' : ''}`}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsCsvDialogOpen(false);
                      setCsvFile(null);
                      setCsvData([]);
                      setCsvErrors([]);
                    }}
                    className="flex-1 rounded-none"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="client-search-input"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-sm border-2 h-10"
            />
          </div>
        </div>

        {/* Clients Grid */}
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : filteredClients.length === 0 ? (
          <Card className="border border-slate-200 rounded-none shadow-none">
            <CardContent className="p-12 text-center">
              <p className="text-slate-500">No clients found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-list">
            {filteredClients.map((client) => (
              <Card key={client.id} className="border border-slate-200 rounded-none shadow-none" data-testid={`client-card-${client.id}`}>
                <CardContent className="p-4 md:p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-heading text-base md:text-lg text-slate-900">{client.name}</h3>
                    <div className="flex gap-1">
                      <Button
                        data-testid={`edit-client-${client.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(client)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        data-testid={`delete-client-${client.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(client.id)}
                        className="h-8 w-8 p-0 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Client ID with Copy Button */}
                  <div className="mb-3 p-2 bg-slate-50 border border-slate-200 rounded-sm">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Client ID</span>
                        <p className="font-mono-data text-xs text-slate-700 truncate">{client.id}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyClientId(client.id)}
                        className="h-7 w-7 p-0 flex-shrink-0"
                        title="Copy Client ID"
                      >
                        {copiedId === client.id ? (
                          <Check className="h-3.5 w-3.5 text-green-600" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    {client.company && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Building2 className="h-4 w-4" />
                        <span>{client.company}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-slate-600">
                      <Mail className="h-4 w-4" />
                      <span>{client.email}</span>
                    </div>
                    {client.phone && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="h-4 w-4" />
                        <span>{client.phone}</span>
                      </div>
                    )}
                  </div>
                  {client.notes && (
                    <p className="text-xs text-slate-500 mt-4 border-t border-slate-100 pt-3">
                      {client.notes}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Clients;